import React from 'react';
import pt from 'prop-types';
import cx from 'classnames';

import { getStorefrontURL, getLocalProxyStorefrontURL } from 'utils/storefront';
import { TEST_ENV, classNames, locationWithQuery, isEmpty } from 'utils';

import { Field } from 'components/form';
import Link from 'components/link';
import Button from 'components/button';
import ButtonLink from 'components/button/link';
import BackButton from 'components/button/back';
import KebabButton from 'components/button/kebab';
import LocaleSelector from 'components/locale/selector';
import StorefrontEditorIcon from 'components/storefront/editor/icon';
import Icon from 'components/icon';
import { SWELL_GLOBAL } from 'components/storefront/editor/utils/easyblocks';
import {
  postEasyblocksFocus,
  postEasyblocksUndo,
  postEasyblocksRedo,
} from 'components/storefront/editor/utils/post-message';
import { getPageUrl } from 'components/storefront/editor/utils/page-data';
import { publishing as storefrontPublishing } from 'components/storefront/editor/utils/storefront';
import { EasyblocksBackendContext } from 'components/storefront/editor/backendContext';

import Status from 'components/status';
import StorefrontEditorHeaderStatus from './status';

import { STOREFRONT_BETA_FEEDBACK_URL } from 'constants/storefront';

import './header.scss';

const ICON_SIZE = 20;
const VIEWPORT = Object.freeze([
  {
    value: 'expand',
    icon: 'expand',
  },
  {
    value: 'fit-screen',
    icon: 'monitor',
  },
  { value: 'md', icon: 'tablet' },
  { value: 'xs', icon: 'phone' },
]);

export default class StorefrontEditorHeader extends React.PureComponent {
  static contextType = EasyblocksBackendContext;

  static propTypes = {
    client: pt.object,
    page: pt.object,
    pageRecord: pt.object,
    params: pt.object,
    storefront: pt.object,
    storefronts: pt.object,
    swellTheme: pt.object,
    router: pt.object,
    location: pt.object,
    session: pt.object,
    viewport: pt.string,
    previewUrl: pt.string,
    loading: pt.bool,
    published: pt.bool,
    publishing: pt.bool,
    showLocaleSelector: pt.bool,
    status: pt.any,
    onPublish: pt.func,
    onSaveThemeLanguage: pt.func,
    onChangeViewport: pt.func,
    inCodeEditor: pt.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      publishing: false,
      checkout: props.location.query.section === 'checkout',
    };
  }

  onClickViewport = (event) => {
    const { viewport, onChangeViewport } = this.props;
    const index = Number(event.currentTarget.dataset.index);
    const viewportValue = VIEWPORT[index]?.value;

    if (!viewportValue || viewport === viewportValue) {
      return;
    }

    onChangeViewport(viewportValue);
  };

  onClickThemeSettings = (event) => {
    event.preventDefault();

    postEasyblocksFocus(SWELL_GLOBAL);
  };

  onClickEditLanguage = (event) => {
    event.preventDefault();

    const { openModal } = this.context;
    const { swellTheme, onSaveThemeLanguage } = this.props;

    openModal('EditThemeLanguage', {
      swellTheme,
      onSave: onSaveThemeLanguage,
    });
  };

  getThemeLanguageConfig() {
    const { swellTheme } = this.props;

    // Maintain backward compatibility for a few theme versions
    // TODO: remove this in the near future
    const languageConfig =
      swellTheme?.globals.translations || swellTheme?.globals.language;

    return !isEmpty(languageConfig) ? languageConfig : null;
  }

  hasThemeLanguageConfig() {
    return Boolean(this.getThemeLanguageConfig());
  }

  onChangeProxy = (_event, checked) => {
    const { location, router } = this.props;

    router.push(
      locationWithQuery(location, { proxy: checked ? 'local' : 'live' }),
    );
  };

  onClickPublish = async (event) => {
    event.preventDefault();

    const { onPublish } = this.props;
    const { publishing } = this.state;

    if (publishing) {
      return;
    }

    this.setState({ publishing: true });
    await onPublish();
    this.setState({ publishing: false });
  };

  publishing() {
    const { storefront } = this.props;

    return (
      this.props.publishing ||
      this.state.publishing ||
      storefrontPublishing(storefront)
    );
  }

  renderPublishButton() {
    const { storefront, loading, published } = this.props;
    const { checkout } = this.state;
    const publishing = this.publishing();

    if (!storefront.primary && !checkout) {
      return null;
    }

    const publishLabel = checkout ? 'Publish checkout' : 'Publish';
    const disabled = loading || published || publishing;

    return (
      <Button
        className="storefront-editor--publish"
        onClick={this.onClickPublish}
        disabled={disabled}
      >
        {publishLabel}
      </Button>
    );
  }

  renderPreviewButton() {
    const { previewUrl, storefront, location, session, loading } = this.props;

    const hasProxy =
      session?.local_proxy?.storefront_id === storefront.id &&
      (!location.query.proxy || location.query.proxy === 'local');

    const storefrontUrl =
      previewUrl ||
      (hasProxy && getLocalProxyStorefrontURL(session)) ||
      getStorefrontURL(storefront, true);

    return (
      <ButtonLink
        className="storefront-editor--preview"
        type="secondary"
        href={
          storefrontUrl +
          (this.props.page
            ? getPageUrl(this.props.page, this.props.pageRecord)
            : '')
        }
        disabled={loading}
        target="_blank"
      >
        Preview
      </ButtonLink>
    );
  }

  renderUndoRedo() {
    const { loading } = this.props;
    const { checkout } = this.state;

    if (checkout) {
      return null;
    }

    return (
      <div className="storefront-editor--undo-redo">
        <Button
          className="button-icon"
          styleType="secondary"
          type="button"
          disabled={loading}
          onClick={postEasyblocksUndo}
        >
          <StorefrontEditorIcon
            type="undo"
            width={ICON_SIZE}
            height={ICON_SIZE}
          />
        </Button>
        <Button
          className="button-icon"
          styleType="secondary"
          type="button"
          disabled={loading}
          onClick={postEasyblocksRedo}
        >
          <StorefrontEditorIcon
            type="redo"
            width={ICON_SIZE}
            height={ICON_SIZE}
          />
        </Button>
      </div>
    );
  }

  renderViewport() {
    const { viewport, loading } = this.props;

    return (
      <Field
        type="radio"
        className="storefront-editor--viewport"
        options={VIEWPORT.map(({ value, icon }) => ({
          value,
          label: (
            <StorefrontEditorIcon
              type={icon}
              width={ICON_SIZE}
              height={ICON_SIZE}
            />
          ),
        }))}
        onClick={this.onClickViewport}
        buttonsWithIcons
        disabled={loading}
        defaultValue={viewport}
      />
    );
  }

  renderLocaleSelector() {
    const { showLocaleSelector } = this.props;

    return (
      <div
        className={cx('storefront-editor--locale-selector', {
          hidden: !showLocaleSelector,
        })}
      >
        <LocaleSelector />
      </div>
    );
  }

  renderLocalProxy() {
    const { session, storefront, location } = this.props;

    if (session?.local_proxy?.storefront_id !== storefront.id) {
      return null;
    }

    return (
      <div className="storefront-editor--proxy-switch">
        <Field
          type="toggle"
          label="Dev mode"
          className="smaller"
          checked={location.query.proxy === 'local'}
          onChange={this.onChangeProxy}
        />
      </div>
    );
  }

  renderStatus() {
    const { saving } = this.context;
    const { status, storefront } = this.props;

    return (
      status || (
        <StorefrontEditorHeaderStatus saving={saving} storefront={storefront} />
      )
    );
  }

  onEnterBack = () => {
    this.setState({ hoverBack: true });
  };

  onLeaveBack = () => {
    this.setState({ hoverBack: false });
  };

  renderStorefrontHeading() {
    const {
      params,
      storefront,
      storefronts: {
        all: { results: storefronts },
      },
      inCodeEditor,
    } = this.props;
    const { hoverBack, checkout: isCheckout } = this.state;
    const { name, theme } = storefront;

    return (
      <div
        className={classNames('storefront-editor--heading', {
          hover: hoverBack,
        })}
        onMouseLeave={this.onLeaveBack}
      >
        <BackButton to="/storefronts" onMouseEnter={this.onEnterBack} />

        <div className="storefront-editor--heading-title">
          <div className="storefront-editor--heading-label">
            <span className="storefront-editor--heading-name">{name}</span>
            <KebabButton
              align="start"
              alignOffset={0}
              button={false}
              items={[
                ...(theme
                  ? [
                      <label key="theme">
                        {theme.name} {theme.version}
                      </label>,
                      <hr key="theme-hr" />,
                    ]
                  : []),
                <Link
                  key="storefront-settings"
                  to={`/storefronts/${storefront.id}`}
                >
                  <span className="with-icon">
                    <Icon fa="gear" />
                    Storefront settings
                  </span>
                </Link>,
                isCheckout ? (
                  <Link
                    to={
                      storefront.hosted
                        ? `/storefronts/${storefront.id}/v1-editor`
                        : `/storefronts/${storefront.id}/editor`
                    }
                  >
                    <span className="with-icon">
                      <Icon fa="store" />
                      Storefront theme editor
                    </span>
                  </Link>
                ) : (
                  <Link
                    to={
                      storefront.hosted
                        ? `/storefronts/${storefront.id}/v1-editor?section=checkout`
                        : `/storefronts/${storefront.id}/editor?section=checkout`
                    }
                  >
                    <span className="with-icon">
                      <Icon fa="shopping-bag" />
                      Checkout theme editor
                    </span>
                  </Link>
                ),
                this.hasThemeLanguageConfig() && (
                  <button
                    key="help"
                    onClick={this.onClickEditLanguage}
                    type="button"
                  >
                    <span className="with-icon">
                      <Icon fa="globe" />
                      Edit language content
                    </span>
                  </button>
                ),
                theme && inCodeEditor ? (
                  <Link to={`/storefronts/${storefront.id}/editor`}>
                    <span className="with-icon">
                      <Icon fa="brush" />
                      Edit theme
                    </span>
                  </Link>
                ) : (
                  <Link to={`/storefronts/${storefront.id}/code`}>
                    <span className="with-icon">
                      <Icon fa="rectangle-code" />
                      Edit code
                    </span>
                  </Link>
                ),
                <button
                  key="help"
                  onClick={this.context.onClickGetHelp}
                  type="button"
                >
                  <span className="with-icon">
                    <Icon fa="circle-question" />
                    Get help
                  </span>
                </button>,
                ...(!isCheckout && storefronts.length > 1
                  ? [
                      <hr key="storefronts-hr" />,
                      <label key="storefronts-label">Storefronts</label>,
                      ...storefronts.map((storefront) => {
                        const isSelf =
                          !isCheckout && storefront.id === params.id;

                        let link = null;

                        if (!isSelf) {
                          link = storefront.hosted
                            ? `/storefronts/${storefront.id}/v1-editor`
                            : `/storefronts/${storefront.id}/editor${
                                inCodeEditor ? '/code' : ''
                              }`;
                        }

                        return (
                          <Link key={storefront.id} to={link}>
                            <span className="with-icon">
                              <Icon fa="store" />
                              {storefront.name}
                              {isSelf && (
                                <>
                                  &nbsp;
                                  <Icon fa="check" />
                                </>
                              )}
                            </span>
                          </Link>
                        );
                      }),
                    ]
                  : []),
              ]}
            />
          </div>
          <Link to="/storefronts" className="button-back">
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  renderBetaFeedbackCta() {
    return (
      this.props.client?.features.has('storefront_v2') && (
        <Status
          dot={false}
          prefixText={'V2 Beta'}
          faIcon={'megaphone'}
          type={null}
          className="storefront-beta-feedback"
        >
          <a
            href={STOREFRONT_BETA_FEEDBACK_URL}
            rel="noopener noreferrer"
            target="blank"
          >
            Give feedback
          </a>
        </Status>
      )
    );
  }

  render() {
    const { inCodeEditor } = this.props;

    return (
      <header
        className={cx('storefront-editor--header', {
          test: Boolean(TEST_ENV),
        })}
      >
        {this.renderStorefrontHeading()}
        {this.renderBetaFeedbackCta()}
        {!inCodeEditor && this.renderLocaleSelector()}
        {!inCodeEditor && this.renderViewport()}

        <div className="storefront-editor--header-actions">
          {!inCodeEditor && this.renderLocalProxy()}
          {this.renderStatus()}
          {!inCodeEditor && this.renderUndoRedo()}
          {this.renderPreviewButton()}
          {!inCodeEditor && this.renderPublishButton()}
        </div>
      </header>
    );
  }
}
